import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
  Card,
  CardMedia,
  Modal,
  Backdrop,
  Fade,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // Import for generic file icon
import { NotificationContext } from "../../context/NotificationContext";
import { messagingApi } from "../../api/messagingApi";
import { API } from "../../api/axios";
import { AuthContext } from "../../context/AuthContext";
import { linkifyText } from "../../utils/linkify"; // Import linkifyText

/* ------------------ Normalize message ------------------ */
const normalizeMessage = (msg) => {
  let attachments = [];
  if (Array.isArray(msg.attachments)) {
    attachments = msg.attachments;
  } else if (typeof msg.attachments === "string") {
    try {
      const parsed = JSON.parse(msg.attachments);
      if (Array.isArray(parsed)) {
        attachments = parsed;
      }
    } catch (e) {
      // Not a valid JSON string or not an array after parsing.
      // attachments remains an empty array.
    }
  }

  return {
    ...msg,
    createdAt: msg.createdAt || new Date().toISOString(),
    attachments: attachments,
  };
};

const MessageInbox = ({ selectedMessage, castingId }) => {
  const sentBubbleColor = "#6e0506";
  const receivedBubbleColor = "#f5f5f5";
  const receivedBubbleTextColor = "#2b2b2b";
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null); // Now stores the full attachment object

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const { userId } = useContext(AuthContext);
  const { latestMessage } = useContext(NotificationContext);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!userId) return;

    API.get(`/user/getUser/${userId}`)
      .then((res) => {
        if (res.data?.success) {
          setUserInfo(res.data.user);
        }
      })
      .catch((err) => console.error(err));
  }, [userId]);

  const getRecipientId = (selectedMessage, userId) => {
    if (!selectedMessage) return null;

    // If message has explicit caller
    if (selectedMessage.caller?.id) {
      return selectedMessage.caller.id;
    }

    // If message has sender & receiver logic
    if (selectedMessage.senderId && selectedMessage.recipientId) {
      return selectedMessage.senderId === userId
        ? selectedMessage.recipientId
        : selectedMessage.senderId;
    }

    // Fallback to casting caller
    if (selectedMessage.casting?.callerId) {
      return selectedMessage.casting.callerId;
    }

    return null;
  };

  useEffect(() => {
    if (!userId) return;
    const recipientId = getRecipientId(selectedMessage, userId);
    if (!recipientId) return;
    API.get(`/user/getUser/${recipientId}`)
      .then((res) => {
        if (res.data?.success) {
          setRecipientInfo(res.data.user);
        }
      })
      .catch((err) => console.error(err));
  }, [userId]);
  const fetchConversation = async () => {
    if (!selectedMessage || !castingId || !userId) return;

    const recipientId = getRecipientId(selectedMessage, userId);

    if (!recipientId) return;

    try {
      setLoading(true);
      console.log(
        "Fetching conversation for castingId:",
        castingId,
        "recipientIdss:",
        recipientId,
      ); // Debug log
      const res = await messagingApi.getConversation(castingId, recipientId);

      if (res.data?.success) {
        setMessages(res.data.data.map(normalizeMessage));
      } else {
        setError("Failed to load messages");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversation();
  }, [selectedMessage, castingId, userId]);

  useEffect(() => {
    if (latestMessage) {
      const recipientId = getRecipientId(selectedMessage, userId);
      const recipientIdOfSelected = recipientId || selectedMessage?.senderId;
      const isForCurrentCasting = latestMessage.castingId === castingId;
      const isForCurrentUser =
        (latestMessage.senderId === userId &&
          latestMessage.recipientId === recipientIdOfSelected) ||
        (latestMessage.senderId === recipientIdOfSelected &&
          latestMessage.recipientId === userId);

      if (
        isForCurrentCasting &&
        isForCurrentUser &&
        !messages.some((msg) => msg.id === latestMessage.id)
      ) {
        setMessages((prev) => [...prev, normalizeMessage(latestMessage)]);
      }
    }
  }, [latestMessage, castingId, selectedMessage, userId, messages]);

  const handleFileChange = (e) => {
    setSelectedFiles([...selectedFiles, ...e.target.files]);
  };

  const removeFile = (fileName) => {
    setSelectedFiles(selectedFiles.filter((file) => file.name !== fileName));
  };

  const containsDisallowedMediaLink = (text = "") => {
    const normalized = text.toLowerCase();
    return /(?:https?:\/\/|www\.)?[^\s]+\.(mp3|mp4)(?:[?#][^\s]*)?/i.test(normalized)
      || /(?:^|\s)(?:[a-z0-9.-]+\.)+(?:mp3|mp4)(?:[/?#][^\s]*)?/i.test(normalized);
  };

  const handleSendMessage = async () => {
    const trimmedMessage = messageText.trim();
    if ((!trimmedMessage && selectedFiles.length === 0) || !userInfo)
      return;

    if (containsDisallowedMediaLink(trimmedMessage)) {
      setError("MP3 and MP4 links are not allowed in messages.");
      return;
    }

    const recipientId = getRecipientId(selectedMessage, userId);
    if (!recipientId) return;
    const tempId = `temp-${Date.now()}`;

    const tempMessage = {
      id: tempId,
      messageText: messageText.trim(),
      senderId: userId,
      sender: userInfo,
      createdAt: new Date().toISOString(),
      attachments: selectedFiles.map((file) => ({
        url: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
      })),
    };

    setMessages((prev) => [...prev, tempMessage]);
    setMessageText("");
    const filesToSend = [...selectedFiles];
    setSelectedFiles([]);
    setSending(true);
    setError(null);

    try {
      const res = await messagingApi.sendMessage(
        castingId,
        recipientId,
        tempMessage.messageText,
        filesToSend,
      );

      if (res.data?.success) {
        const serverMessage = normalizeMessage(res?.data?.data);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === tempId ? serverMessage : msg)),
        );
      } else {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
        setError("Failed to send message");
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      setError("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleOpenModal = (attachment) => {
    setSelectedImage(attachment); // Store the full attachment object
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await fetch(attachment.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = attachment.name || "download";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const recipientName = recipientInfo
    ? `${recipientInfo.firstName} ${recipientInfo.lastName}`
    : "User";

  const recipientAvatar = recipientInfo?.imageURL || "";

  if (!selectedMessage || !castingId) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography>Select a conversation</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ height: "600px", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        <Avatar src={recipientAvatar} />
        <Box>
          <Typography fontWeight={600}>{recipientName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedMessage?.casting?.title || "Casting Call"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Typography textAlign="center" color="text.secondary">
            No messages yet
          </Typography>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderId === userId;
            const timeValid = !isNaN(new Date(msg.createdAt));

            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isMe ? "flex-end" : "flex-start",
                  mb: 1.5,
                  gap: 1,
                }}
              >
                {!isMe && (
                  <Avatar
                    src={msg.sender?.imageURL}
                    sx={{ width: 30, height: 30 }}
                  />
                )}

                <Paper
                  sx={{
                    px: 2,
                    py: 1,
                    maxWidth: "65%",
                    bgcolor: isMe ? sentBubbleColor : receivedBubbleColor,
                    color: isMe ? "#fff" : receivedBubbleTextColor,
                    borderRadius: isMe
                      ? "16px 16px 4px 16px"
                      : "16px 16px 16px 4px",
                  }}
                >
                  {msg?.attachments && msg?.attachments?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {msg?.attachments?.length > 0 &&
                        msg?.attachments.map((att, index) => (
                          <Box key={index} sx={{ position: "relative" }}>
                            {att.type && att.type.startsWith("image/") ? (
                              <img
                                src={att.url}
                                alt={att.name || `attachment ${index}`}
                                style={{
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                  marginTop: "8px",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleOpenModal(att)} // Pass full attachment object
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  bgcolor: "#e0e0e0",
                                  p: 1,
                                  borderRadius: "8px",
                                  marginTop: "8px",
                                }}
                              >
                                <InsertDriveFileIcon />
                                <Typography variant="body2">
                                  {att.name || "File"}
                                </Typography>
                              </Box>
                            )}
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top:
                                  att.type && att.type.startsWith("image/")
                                    ? 8
                                    : 4, // Adjust position for file icon
                                right:
                                  att.type && att.type.startsWith("image/")
                                    ? 8
                                    : 4,
                                bgcolor: "rgba(0,0,0,0.5)",
                                color: "white",
                              }}
                              onClick={() => handleDownload(att)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                    </Box>
                  )}
                  <Typography variant="body2">
                    {linkifyText(msg.messageText)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      opacity: 0.7,
                      mt: 0.5,
                    }}
                  >
                    {timeValid
                      ? new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "Sending..."}
                  </Typography>
                </Paper>

                {isMe && (
                  <Avatar
                    src={userInfo?.imageURL}
                    sx={{ width: 30, height: 30 }}
                  />
                )}
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              outline: "none",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            {selectedImage &&
            selectedImage.url &&
            selectedImage.type &&
            selectedImage.type.startsWith("image/") ? (
              <img
                src={selectedImage.url}
                alt={selectedImage.name || "enlarged"}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "200px", // A minimum height to make the box visible
                  bgcolor: "#f0f0f0",
                  p: 2,
                  textAlign: "center",
                }}
              >
                <InsertDriveFileIcon fontSize="large" sx={{ mb: 1 }} />
                <Typography variant="h6">
                  {selectedImage?.name || "File Preview Not Available"}
                </Typography>
                <Typography variant="body2">
                  Only images can be previewed in the modal. Please download the
                  file.
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={() => handleDownload(selectedImage)} // Pass full attachment object
              sx={{ mt: 2 }}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
              onClick={handleCloseModal}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Fade>
      </Modal>

      <Divider />
      {selectedFiles.length > 0 && (
        <Box sx={{ p: 2, display: "flex", gap: 1, overflowX: "auto" }}>
          {selectedFiles.map((file) => (
            <Card
              key={file.name}
              sx={{ position: "relative", width: 100, flexShrink: 0 }}
            >
              {file.type.startsWith("image/") ? (
                <CardMedia
                  component="img"
                  height="100"
                  image={URL.createObjectURL(file)}
                  alt={file.name}
                />
              ) : (
                <Box
                  sx={{
                    height: 100,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f0f0f0",
                    p: 1,
                  }}
                >
                  <InsertDriveFileIcon fontSize="large" />
                  <Typography
                    variant="caption"
                    textAlign="center"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {file.name}
                  </Typography>
                </Box>
              )}
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                }}
                onClick={() => removeFile(file.name)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Box>
      )}

      {/* <Box sx={{ p: 2, display: "flex", alignItems: "stretch", gap: 1 }}>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <Tooltip title="Attach file">
          <span>
            <IconButton
              onClick={() => fileInputRef.current.click()}
              sx={{ alignSelf: "center" }}
            >
              <AttachFileIcon />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          fullWidth
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          multiline
          maxRows={3}
        />

        <Tooltip title="Send">
          <span style={{ display: "flex" }}>
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={
                (!messageText.trim() && selectedFiles.length === 0) ||
                sending ||
                !userInfo
              }
              sx={{
                height: "100%",
                minWidth: "56px",
                padding: 0,
              }}
            >
              {sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </span>
        </Tooltip>
      </Box> */}
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "stretch",
          gap: 1,
          flexWrap: "nowrap",

          "@media (max-width:600px)": {
            p: 0,
            gap: 0.5,
          },
        }}
      >
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <Tooltip title="Attach file">
          <span>
            <IconButton
              onClick={() => fileInputRef.current.click()}
              sx={{
                alignSelf: "center",
                p: { xs: 1, sm: 1.5 },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          fullWidth
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          multiline
          maxRows={3}
          size="small"
          sx={{
            "& .MuiInputBase-root": {
              borderRadius: "12px",
              fontSize: { xs: "14px", sm: "16px" },
            },
          }}
        />

        <Tooltip title="Send">
          <span style={{ display: "flex" }}>
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={
                (!messageText.trim() && selectedFiles.length === 0) ||
                sending ||
                !userInfo
              }
              sx={{
                height: "100%",
                minWidth: { xs: "44px", sm: "56px" },
                borderRadius: "10px",
                p: 0,
              }}
            >
              {sending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Typography color="error" textAlign="center" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
    </Paper>
  );
};

export default MessageInbox;
